
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, BarChart2, PieChart, LineChart, Presentation, Download, FileBarChart, FilePieChart } from "lucide-react";
import { useBranding } from "@/contexts/BrandingContext";

export function ReportsGenerator() {
  const { branding } = useBranding();
  const [activeTab, setActiveTab] = useState("financial");
  const [reportType, setReportType] = useState("valuation");
  const [presentationType, setPresentationType] = useState("investor");
  const [dateRange, setDateRange] = useState("current-quarter");
  const [generating, setGenerating] = useState(false);
  const [reportFormat, setReportFormat] = useState("pdf");

  const handleGenerateReport = () => {
    setGenerating(true);
    // Simulate report generation
    setTimeout(() => {
      setGenerating(false);
      // Here you would normally handle the download or display of the generated report
      alert(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report generated successfully!`);
    }, 1500);
  };

  const handleGeneratePresentation = () => {
    setGenerating(true);
    // Simulate presentation generation
    setTimeout(() => {
      setGenerating(false);
      // Here you would normally handle the download or display of the generated presentation
      alert(`${presentationType.charAt(0).toUpperCase() + presentationType.slice(1)} presentation generated successfully!`);
    }, 1500);
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center gap-3 mb-6">
        <img src={branding.logoUrl} alt={branding.name} className="h-10 w-auto" />
        <div>
          <h1 className="text-3xl font-bold">{branding.name} Reports</h1>
          <p className="text-muted-foreground">
            Generate comprehensive financial reports and presentations
          </p>
        </div>
      </div>

      <Tabs defaultValue="financial" onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="financial">Financial Reports</TabsTrigger>
          <TabsTrigger value="resource">Resource Reports</TabsTrigger>
          <TabsTrigger value="presentations">Presentations</TabsTrigger>
        </TabsList>

        <TabsContent value="financial" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileBarChart className="h-5 w-5" />
                Financial Reports
              </CardTitle>
              <CardDescription>
                Generate detailed financial reports for your mining assets and operations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="report-type">Report Type</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger id="report-type">
                    <SelectValue placeholder="Select report type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="valuation">Asset Valuation Report</SelectItem>
                    <SelectItem value="cashflow">Cash Flow Analysis</SelectItem>
                    <SelectItem value="investment">Investment Performance</SelectItem>
                    <SelectItem value="tax">Tax Implications Report</SelectItem>
                    <SelectItem value="forecast">5-Year Financial Forecast</SelectItem>
                    <SelectItem value="roi">ROI Analysis</SelectItem>
                    <SelectItem value="capex">Capital Expenditure Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date-range">Time Period</Label>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger id="date-range">
                    <SelectValue placeholder="Select time period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current-quarter">Current Quarter</SelectItem>
                    <SelectItem value="ytd">Year to Date</SelectItem>
                    <SelectItem value="last-year">Last Financial Year</SelectItem>
                    <SelectItem value="3-year">3-Year Historical</SelectItem>
                    <SelectItem value="5-year">5-Year Historical</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {dateRange === "custom" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input id="start-date" type="date" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input id="end-date" type="date" />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="include-charts">Include Visualizations</Label>
                <Select defaultValue="standard">
                  <SelectTrigger id="include-charts">
                    <SelectValue placeholder="Select chart options" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Minimal (Text Only)</SelectItem>
                    <SelectItem value="standard">Standard Charts</SelectItem>
                    <SelectItem value="detailed">Detailed Analytics</SelectItem>
                    <SelectItem value="interactive">Interactive Dashboards</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="report-format">Report Format</Label>
                <Select value={reportFormat} onValueChange={setReportFormat}>
                  <SelectTrigger id="report-format">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF Report</SelectItem>
                    <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                    <SelectItem value="web">Web Dashboard</SelectItem>
                    <SelectItem value="csv">CSV Data Export</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleGenerateReport}
                disabled={generating}
                className="w-full"
                style={{ backgroundColor: generating ? undefined : branding.primaryColor }}
              >
                {generating ? (
                  <>Generating Report...</>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center">
                  <BarChart2 className="h-4 w-4 mr-2" />
                  Valuation Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Asset valuation based on current market conditions and resource estimates.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center">
                  <LineChart className="h-4 w-4 mr-2" />
                  Cash Flow Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Detailed analysis of capital inflows and outflows with projections.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center">
                  <PieChart className="h-4 w-4 mr-2" />
                  Investment Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Performance tracking for investments and funding allocations.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="resource" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FilePieChart className="h-5 w-5" />
                Resource Reports
              </CardTitle>
              <CardDescription>
                Generate detailed reports about your mineral resources and reserves.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="resource-report-type">Report Type</Label>
                <Select defaultValue="jorc">
                  <SelectTrigger id="resource-report-type">
                    <SelectValue placeholder="Select report type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jorc">JORC Compliance Report</SelectItem>
                    <SelectItem value="resource">Resource Estimation</SelectItem>
                    <SelectItem value="reserves">Reserves Statement</SelectItem>
                    <SelectItem value="exploration">Exploration Results</SelectItem>
                    <SelectItem value="geological">Geological Assessment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenement">Tenement/Project</Label>
                <Select defaultValue="all">
                  <SelectTrigger id="tenement">
                    <SelectValue placeholder="Select tenement" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tenements</SelectItem>
                    <SelectItem value="project-a">Northern Basin Project</SelectItem>
                    <SelectItem value="project-b">Eastern Ridge Exploration</SelectItem>
                    <SelectItem value="project-c">Southern Fields Development</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="resource-detail">Detail Level</Label>
                <Select defaultValue="standard">
                  <SelectTrigger id="resource-detail">
                    <SelectValue placeholder="Select detail level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summary">Summary Overview</SelectItem>
                    <SelectItem value="standard">Standard Report</SelectItem>
                    <SelectItem value="detailed">Detailed Technical Report</SelectItem>
                    <SelectItem value="compliance">Full Compliance Documentation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="include-maps">Include Maps & Visuals</Label>
                <Select defaultValue="standard">
                  <SelectTrigger id="include-maps">
                    <SelectValue placeholder="Select map options" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Basic Location Maps</SelectItem>
                    <SelectItem value="standard">Standard Geological Maps</SelectItem>
                    <SelectItem value="detailed">Detailed 3D Models</SelectItem>
                    <SelectItem value="comprehensive">Comprehensive Visual Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleGenerateReport}
                disabled={generating}
                className="w-full"
                style={{ backgroundColor: generating ? undefined : branding.primaryColor }}
              >
                {generating ? (
                  <>Generating Report...</>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Generate Resource Report
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">JORC Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Reports that meet JORC Code requirements for public reporting.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Resource Estimation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Detailed resource estimates with confidence categories and geological context.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Exploration Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Comprehensive reports on exploration activities and findings.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="presentations" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Presentation className="h-5 w-5" />
                Presentations
              </CardTitle>
              <CardDescription>
                Generate ready-to-present slide decks for investors, board meetings, and stakeholders.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="presentation-type">Presentation Type</Label>
                <Select value={presentationType} onValueChange={setPresentationType}>
                  <SelectTrigger id="presentation-type">
                    <SelectValue placeholder="Select presentation type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="investor">Investor Pitch Deck</SelectItem>
                    <SelectItem value="board">Board Meeting</SelectItem>
                    <SelectItem value="quarterly">Quarterly Review</SelectItem>
                    <SelectItem value="project">Project Overview</SelectItem>
                    <SelectItem value="strategic">Strategic Planning</SelectItem>
                    <SelectItem value="compliance">Compliance Presentation</SelectItem>
                    <SelectItem value="market">Market Analysis</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="slides-count">Number of Slides</Label>
                <Select defaultValue="medium">
                  <SelectTrigger id="slides-count">
                    <SelectValue placeholder="Select slide count" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Brief (5-10 slides)</SelectItem>
                    <SelectItem value="medium">Standard (10-20 slides)</SelectItem>
                    <SelectItem value="detailed">Comprehensive (20+ slides)</SelectItem>
                    <SelectItem value="custom">Custom Count</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="include-financials">Financial Detail Level</Label>
                <Select defaultValue="summary">
                  <SelectTrigger id="include-financials">
                    <SelectValue placeholder="Select financial detail level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">High-Level Only</SelectItem>
                    <SelectItem value="summary">Summary Charts</SelectItem>
                    <SelectItem value="detailed">Detailed Financials</SelectItem>
                    <SelectItem value="comprehensive">Comprehensive Analysis</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="presentation-format">Output Format</Label>
                <Select defaultValue="pptx">
                  <SelectTrigger id="presentation-format">
                    <SelectValue placeholder="Select output format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pptx">PowerPoint (PPTX)</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="google">Google Slides</SelectItem>
                    <SelectItem value="keynote">Apple Keynote</SelectItem>
                    <SelectItem value="html">Interactive HTML</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="presentation-style">Branding Style</Label>
                <Select defaultValue="corporate">
                  <SelectTrigger id="presentation-style">
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="modern">Modern & Bold</SelectItem>
                    <SelectItem value="minimal">Minimalist</SelectItem>
                    <SelectItem value="data-rich">Data-Rich</SelectItem>
                    <SelectItem value="custom">Custom Branding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleGeneratePresentation}
                disabled={generating}
                className="w-full"
                style={{ backgroundColor: generating ? undefined : branding.primaryColor }}
              >
                {generating ? (
                  <>Generating Presentation...</>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Generate Presentation
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Investor Presentations</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Compelling investor decks highlighting growth potential and asset valuation.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Project Overviews</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Visual presentations of project status, milestones, and resource estimates.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Board Meeting Decks</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Comprehensive board materials with strategic insights and financial analysis.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
